import { ChevronRight } from "lucide-react";
import { Card } from "../../components/ui/patrones";
import { Etiqueta } from "../../components/ui/primitivos";
import type { PresupuestoListado } from "../../lib/consola-api";
import { pesos } from "../../lib/formato";

/**
 * Las otras dos vistas del listado de presupuestos.
 *
 * `imprenta` tiene tres —`table`, `cards` y `list`— y la preferencia vive en
 * `localStorage`. La tabla es `DataTable`; estas dos van acá, como en el tablero
 * de producción.
 *
 * **`list` no es una tabla angosta**: es una fila por presupuesto con el número
 * grande, pensada para leer en pantallas chicas. La diferencia con `cards` es
 * densidad, y por eso existen las dos.
 */

export const TONO: Record<string, "ok" | "aviso" | "error" | "neutral" | "info"> = {
  BORRADOR: "neutral",
  PENDIENTE: "aviso",
  APROBADO: "ok",
  // Azul, como `imprenta` (`EN_PRODUCCION: bg-blue-100`). Estaba en `ok`, o sea
  // el MISMO verde que APROBADO: el chip no distinguía «lo aprobaron» de «lo
  // están haciendo», y encima quedaba en contra de su propia tarjeta, que sí es
  // azul.
  EN_PRODUCCION: "info",
  RECHAZADO: "error",
  CANCELADO: "error",
  COMPLETADO: "neutral",
};

/**
 * Cómo se llama cada estado de consulta en la interfaz.
 *
 * Porteado **literal** de `ESTADO_PRESUPUESTO_LABELS` en
 * `constants/presupuesto.ts` de `imprenta`. Hasta acá se mostraba el valor del
 * enum: `EN_PRODUCCION` con guion bajo y en mayúsculas, que es formato de base.
 */
const ETIQUETA_ESTADO: Record<string, string> = {
  BORRADOR: "Borrador",
  PENDIENTE: "Pendiente",
  APROBADO: "Aprobado",
  RECHAZADO: "Rechazado",
  EN_PRODUCCION: "En Producción",
  CANCELADO: "Cancelado",
  COMPLETADO: "Completado",
};

/** Un estado sin etiqueta devuelve su código, no un hueco (ADR 0085). */
export function etiquetaDeEstadoConsulta(estado: string): string {
  return ETIQUETA_ESTADO[estado] ?? estado;
}

/** El total, o el aviso de que todavía no se puede calcular. */
export function Total({ monto }: { monto: string | null }) {
  if (monto === null) return <span className="text-amber-600 text-sm">a definir</span>;
  return <span className="font-medium tabular-nums">{pesos(monto)}</span>;
}

function Renglones({ cantidad }: { cantidad: number }) {
  return (
    <span className="text-neutral-500 text-sm">
      {cantidad} {cantidad === 1 ? "renglón" : "renglones"}
    </span>
  );
}

export function VistaTarjetas({
  presupuestos,
  alAbrir,
}: {
  presupuestos: readonly PresupuestoListado[];
  alAbrir: (p: PresupuestoListado) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {presupuestos.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => alAbrir(p)}
          className="rounded-xl border border-neutral-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-neutral-300 hover:bg-neutral-50"
        >
          <div className="flex items-start justify-between gap-2">
            <span className="font-mono text-neutral-500 text-sm">{p.numero ?? "sin número"}</span>
            <Etiqueta tono={TONO[p.estado] ?? "neutral"}>
              {etiquetaDeEstadoConsulta(p.estado)}
            </Etiqueta>
          </div>
          <p className="mt-2 truncate font-semibold text-neutral-900">{p.cliente}</p>
          <div className="mt-3 flex items-baseline justify-between">
            <Total monto={p.montoTotal} />
            <Renglones cantidad={p.items} />
          </div>
        </button>
      ))}
    </div>
  );
}

export function VistaLista({
  presupuestos,
  alAbrir,
}: {
  presupuestos: readonly PresupuestoListado[];
  alAbrir: (p: PresupuestoListado) => void;
}) {
  return (
    <Card className="divide-y divide-neutral-200 overflow-hidden">
      {presupuestos.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => alAbrir(p)}
          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-neutral-900 text-sm">{p.numero ?? "sin número"}</span>
              <Etiqueta tono={TONO[p.estado] ?? "neutral"}>
                {etiquetaDeEstadoConsulta(p.estado)}
              </Etiqueta>
            </div>
            <p className="truncate text-neutral-600 text-sm">{p.cliente}</p>
          </div>
          <div className="shrink-0 text-right">
            <Total monto={p.montoTotal} />
            <p>
              <Renglones cantidad={p.items} />
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
        </button>
      ))}
    </Card>
  );
}

export function SinResultados({ mensaje }: { mensaje: string }) {
  return (
    <Card className="p-8 text-center text-neutral-500 text-sm">
      <p>{mensaje}</p>
    </Card>
  );
}
